import * as React from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Loader2, Stethoscope } from 'lucide-react';

import { useAuth } from '@/features/auth/contexto';
import { ErroApi } from '@/lib/api';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';

const esquema = z.object({
  email: z.string().min(1, 'Informe o e-mail.').email('Informe um e-mail válido.'),
  senha: z.string().min(1, 'Informe a senha.'),
});

type Formulario = z.infer<typeof esquema>;

export function PaginaLogin() {
  const { usuario, entrar, carregando } = useAuth();
  const navegar = useNavigate();
  const local = useLocation();
  const [erroServidor, setErroServidor] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: { email: '', senha: '' },
  });

  if (!carregando && usuario) {
    return <Navigate to="/" replace />;
  }

  const aoEnviar = handleSubmit(async (dados) => {
    setErroServidor(null);

    try {
      const autenticado = await entrar(dados.email, dados.senha);
      const destino = (local.state as { de?: string } | null)?.de ?? '/';
      void navegar(autenticado.precisaTrocarSenha ? '/trocar-senha' : destino, { replace: true });
    } catch (erro) {
      setErroServidor(
        erro instanceof ErroApi
          ? erro.mensagemUsuario
          : 'Não foi possível conectar ao servidor. Verifique sua rede e tente novamente.',
      );
    }
  });

  return (
    <main className="bg-fundo flex min-h-dvh items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <span className="bg-marca text-marca-contraste flex size-14 items-center justify-center rounded-2xl">
            <Stethoscope className="size-7" aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">KAVI</h1>
            <p className="text-silenciado-texto text-sm">
              Solicitação de Agendamentos com Especialistas
            </p>
          </div>
        </div>

        <form
          onSubmit={(evento) => void aoEnviar(evento)}
          noValidate
          className="bg-cartao flex flex-col gap-4 rounded-xl border p-6 shadow-sm"
        >
          <div>
            <h2 className="text-lg font-semibold">Entrar</h2>
            <p className="text-silenciado-texto text-sm">
              Use as credenciais fornecidas pela administração.
            </p>
          </div>

          {erroServidor && (
            <div
              role="alert"
              className="border-destrutivo/30 bg-destrutivo/10 text-destrutivo flex items-start gap-2 rounded-md border p-3 text-sm"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>{erroServidor}</span>
            </div>
          )}

          <Campo id="email" rotulo="E-mail" obrigatorio erro={errors.email?.message}>
            <Entrada
              type="email"
              autoComplete="username"
              autoFocus
              placeholder="seu.nome@exemplo.gov.br"
              {...register('email')}
            />
          </Campo>

          <Campo id="senha" rotulo="Senha" obrigatorio erro={errors.senha?.message}>
            <Entrada type="password" autoComplete="current-password" {...register('senha')} />
          </Campo>

          <Botao type="submit" disabled={isSubmitting} className="mt-1">
            {isSubmitting && <Loader2 className="animate-spin" aria-hidden="true" />}
            {isSubmitting ? 'Entrando…' : 'Entrar'}
          </Botao>

          <Link
            to="/esqueci-senha"
            className="text-marca text-center text-sm underline-offset-4 hover:underline"
          >
            Esqueci minha senha
          </Link>
        </form>

        <p className="text-silenciado-texto mt-6 text-center text-xs">
          Sistema de uso restrito. Os acessos são registrados em auditoria.
        </p>
      </div>
    </main>
  );
}
